/**
 * EOS termination detail page (M18.8) at /operations/eos/{id}.
 *
 * Sections:
 *   - Header with workflow state badge + next-action button
 *   - EOS calculation summary (engine-resolved amounts)
 *   - Final settlement line items
 *   - Exit clearance checklist (complete/waive inline)
 *   - Government filing drafts (JSON view)
 *   - Workflow event timeline
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardList, FileText, Wallet } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  ApprovalFlowDiagram,
  EmptyState,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { api } from "../lib/api";
import type {
  ExitClearanceItem,
  GovernmentFilingDetail,
  TerminationDetail,
  WorkflowEventEntry,
} from "../lib/api";

function StateAction({
  term,
  onChanged,
}: {
  term: TerminationDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const route = useMutation({
    mutationFn: () =>
      api.routeTermination(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const approve = useMutation({
    mutationFn: () =>
      api.approveTermination(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const begin = useMutation({
    mutationFn: () =>
      api.beginExitClearance(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const draft = useMutation({
    mutationFn: () => api.draftFilings(term.id),
    onSuccess: onChanged,
  });
  const markDrafted = useMutation({
    mutationFn: () =>
      api.markFilingsDrafted(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const finalPayslip = useMutation({
    mutationFn: () =>
      api.generateFinalPayslip(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const close = useMutation({
    mutationFn: () => api.closeTermination(term.id, { actor: "hr" }),
    onSuccess: onChanged,
  });

  const cls =
    "rounded bg-brand px-3 py-1.5 text-xs font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50";

  switch (term.workflow_state) {
    case "settlement_calculated":
      return (
        <button
          type="button"
          onClick={() => route.mutate()}
          disabled={route.isPending}
          className={cls}
        >
          {route.isPending
            ? t("operations.eos_routing")
            : t("operations.eos_route_for_approval")}
        </button>
      );
    case "pending_hr_review":
      return (
        <button
          type="button"
          onClick={() => approve.mutate()}
          disabled={approve.isPending}
          className={cls}
        >
          {approve.isPending
            ? t("operations.eos_approving")
            : t("operations.eos_approve")}
        </button>
      );
    case "approved":
      return (
        <button
          type="button"
          onClick={() => begin.mutate()}
          disabled={begin.isPending}
          className={cls}
        >
          {begin.isPending
            ? t("operations.eos_starting")
            : t("operations.eos_begin_exit_clearance")}
        </button>
      );
    case "exit_clearance_in_progress":
      return (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => draft.mutate()}
            disabled={draft.isPending}
            className="rounded border border-border-default px-3 py-1.5 text-xs hover:bg-surface-muted disabled:opacity-50"
          >
            {draft.isPending
              ? t("operations.eos_drafting")
              : t("operations.eos_draft_gov_filings")}
          </button>
          <button
            type="button"
            onClick={() => markDrafted.mutate()}
            disabled={markDrafted.isPending}
            className={cls}
          >
            {markDrafted.isPending
              ? t("operations.eos_advancing")
              : t("operations.eos_mark_filings_drafted")}
          </button>
        </div>
      );
    case "gov_filings_drafted":
      return (
        <button
          type="button"
          onClick={() => finalPayslip.mutate()}
          disabled={finalPayslip.isPending}
          className={cls}
        >
          {finalPayslip.isPending
            ? t("operations.eos_generating")
            : t("operations.eos_generate_final_payslip")}
        </button>
      );
    case "final_payslip_generated":
      return (
        <button
          type="button"
          onClick={() => close.mutate()}
          disabled={close.isPending}
          className={cls}
        >
          {close.isPending
            ? t("operations.eos_closing")
            : t("operations.eos_close_termination")}
        </button>
      );
    default:
      return null;
  }
}

function Eos12ComponentBreakdownPreview({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  const [breakdown, setBreakdown] = React.useState<
    import("../lib/api").SettlementBreakdownDTO | null
  >(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  async function loadPreview() {
    if (!term.eos || !term.settlement) return;
    setLoading(true);
    setErr(null);
    try {
      const { api } = await import("../lib/api");
      const triggerType =
        term.termination_type === "termination"
          ? "termination_without_cause"
          : term.termination_type;
      // Pull last_actual_wage from the audit trace if present;
      // fall back to eos_amount / service_years approximation
      // for a reasonable preview.
      const trace = (term.eos?.audit_trace ?? {}) as Record<string, unknown>;
      const lastWage =
        (trace.last_actual_wage as string | undefined) ?? "5000";
      const hireD =
        (trace.hire_date as string | undefined) ??
        new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      const exitD =
        term.eos?.last_working_day ?? new Date().toISOString().slice(0, 10);

      const bd = await api.previewEosSettlement({
        employee_id: term.employee_id,
        legal_entity_id: term.legal_entity_id,
        trigger_type: triggerType,
        last_actual_wage: lastWage,
        hire_date: hireD,
        exit_date: exitD,
        last_payroll_close: exitD,
        leave_encashment_amount: term.settlement?.leave_encashment_amount ?? "0",
        ticket_entitlement_value: term.settlement?.ticket_entitlement_value ?? "0",
        outstanding_loan_balance: term.settlement?.outstanding_loan_balance ?? "0",
        pending_allowances: term.settlement?.pending_allowances ?? "0",
        pending_deductions: term.settlement?.pending_deductions ?? "0",
      });
      setBreakdown(bd);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          {t(
            "operations.eos_12_component_breakdown",
            "12-component DET breakdown (F.15)",
          )}
        </h3>
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading || !term.eos || !term.settlement}
          className="rounded-md border border-brand-blue px-3 py-1 text-xs text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
        >
          {loading
            ? t("operations.eos_12_loading", "Computing…")
            : t("operations.eos_12_compute", "Compute breakdown")}
        </button>
      </div>
      {err && (
        <p className="rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          {err}
        </p>
      )}
      {breakdown && (
        <SettlementBreakdownTableLazy breakdown={breakdown} />
      )}
    </div>
  );
}

const SettlementBreakdownTableLazy = React.lazy(() =>
  import("../components/eos/SettlementBreakdownTable").then((m) => ({
    default: m.SettlementBreakdownTable,
  })),
);

const Article81ProtectionBadgeLazy = React.lazy(() =>
  import("../components/eos/Article81ProtectionBadge").then((m) => ({
    default: m.Article81ProtectionBadge,
  })),
);

const NoticePeriodSubWorkflowLazy = React.lazy(() =>
  import("../components/eos/NoticePeriodSubWorkflow").then((m) => ({
    default: m.NoticePeriodSubWorkflow,
  })),
);

function EosSettlementCard({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  if (!term.eos || !term.settlement) {
    return (
      <EmptyState
        icon={Wallet}
        headline={t("operations.eos_no_eos_computed")}
        description={t("operations.eos_no_eos_body")}
      />
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <article className="rounded-lg border border-border-subtle bg-surface-default p-4">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("operations.eos_calculation")}
        </h3>
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-text-secondary">{t("operations.eos_service_years")}</dt>
          <dd className="font-mono">{term.eos.service_years}</dd>
          <dt className="text-text-secondary">{t("operations.eos_service_months")}</dt>
          <dd className="font-mono">{term.eos.service_months}</dd>
          <dt className="text-text-secondary">{t("operations.eos_last_working_day")}</dt>
          <dd className="font-mono">{term.eos.last_working_day}</dd>
          <dt className="text-text-secondary">{t("operations.eos_amount_label")}</dt>
          <dd
            className="font-mono font-semibold"
            data-testid="eos-gratuity-amount"
          >
            {term.eos.eos_amount} {term.eos.eos_currency}
          </dd>
        </dl>
      </article>
      <article className="rounded-lg border border-border-subtle bg-surface-default p-4">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("operations.eos_final_settlement")}
        </h3>
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-text-secondary">{t("operations.eos_leave_encashment")}</dt>
          <dd className="font-mono">
            {term.settlement.leave_encashment_amount}
          </dd>
          <dt className="text-text-secondary">{t("operations.eos_outstanding_loans")}</dt>
          <dd className="font-mono">
            {term.settlement.outstanding_loan_balance}
          </dd>
          <dt className="text-text-secondary">{t("operations.eos_ticket_entitlement")}</dt>
          <dd className="font-mono">
            {term.settlement.ticket_entitlement_value}
          </dd>
          <dt className="text-text-secondary">{t("operations.eos_gratuity_other")}</dt>
          <dd className="font-mono">{term.settlement.gratuity_other}</dd>
          <dt className="text-text-secondary font-semibold">{t("operations.eos_total")}</dt>
          <dd className="font-mono font-semibold text-text-primary">
            {term.settlement.total_settlement_amount}{" "}
            {term.settlement.currency}
          </dd>
          <dt className="text-text-secondary">{t("operations.eos_status_label")}</dt>
          <dd className="font-mono">{term.settlement.status}</dd>
        </dl>
      </article>
    </div>
  );
}

function ClearanceItemRow({
  termId,
  item,
  onChanged,
}: {
  termId: string;
  item: ExitClearanceItem;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const complete = useMutation({
    mutationFn: () =>
      api.completeClearanceItem(termId, item.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  return (
    <li
      data-testid={`clearance-item-${item.item_type}`}
      className="flex items-center justify-between gap-2 border-b border-border-subtle py-2 last:border-0"
    >
      <div className="flex-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-text-secondary">
            {item.item_type}
          </span>
          <span>{item.description}</span>
          {item.status !== "pending" && (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">
              {item.status}
            </span>
          )}
        </div>
        {item.notes && (
          <p className="mt-1 text-xs italic text-text-secondary">
            "{item.notes}"
          </p>
        )}
      </div>
      {item.status === "pending" && (
        <button
          type="button"
          onClick={() => complete.mutate()}
          disabled={complete.isPending}
          className="flex items-center gap-1 rounded border border-border-default px-2 py-1 text-xs hover:bg-surface-muted disabled:opacity-50"
        >
          <Check size={12} /> {t("operations.eos_complete")}
        </button>
      )}
    </li>
  );
}

function ClearanceCard({
  termId,
  onChanged,
}: {
  termId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["clearance", termId],
    queryFn: () => api.getExitClearance(termId),
  });
  if (q.isLoading) return <Loading />;
  if (q.error)
    return <ErrorPanel message={(q.error as Error).message} />;
  const data = q.data!;
  if (!data.clearance) {
    return (
      <EmptyState
        icon={ClipboardList}
        headline={t("operations.eos_no_exit_clearance")}
        description={t("operations.eos_no_exit_clearance_body")}
      />
    );
  }
  return (
    <article className="rounded-lg border border-border-subtle bg-surface-default p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("operations.eos_exit_clearance")}
        </h3>
        <span className="text-xs text-text-secondary">
          {t("operations.eos_status_prefix")} {data.clearance.status}{" "}
          {data.is_complete && (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">
              {t("operations.eos_complete_status")}
            </span>
          )}
        </span>
      </header>
      <ul className="mt-2">
        {data.items.map((item) => (
          <ClearanceItemRow
            key={item.id}
            termId={termId}
            item={item}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </article>
  );
}

function FilingsCard({ termId }: { termId: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["filings", termId],
    queryFn: () => api.listTerminationFilings(termId),
  });
  if (q.isLoading) return <Loading />;
  if (q.error)
    return <ErrorPanel message={(q.error as Error).message} />;
  const filings: GovernmentFilingDetail[] = q.data ?? [];
  if (filings.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        headline={t("operations.eos_no_filings_drafted")}
        description={t("operations.eos_no_filings_body")}
      />
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {filings.map((f) => (
        <article
          key={f.id}
          className="rounded-lg border border-border-subtle bg-surface-default p-3 text-xs"
        >
          <header className="flex items-center gap-2">
            <FileText size={14} className="text-text-secondary" />
            <span className="font-semibold">{f.filing_type}</span>
            <span className="rounded bg-surface-muted px-1.5 py-0.5">
              {f.status}
            </span>
            {Object.keys(f.errors || {}).length > 0 && (
              <span className="rounded bg-rose-100 px-1.5 py-0.5 text-rose-700">
                {t("operations.eos_validation_errors", {
                  count: Object.keys(f.errors).length,
                })}
              </span>
            )}
          </header>
          <details className="mt-2">
            <summary className="cursor-pointer text-text-secondary">
              {t("operations.eos_show_payload")}
            </summary>
            <pre className="mt-1 overflow-x-auto rounded bg-surface-muted p-2 font-mono text-[10px]">
              {JSON.stringify(f.payload_json, null, 2)}
            </pre>
          </details>
        </article>
      ))}
    </div>
  );
}

function HistoryCard({ termId }: { termId: string }) {
  const q = useQuery({
    queryKey: ["history", termId],
    queryFn: () => api.getTerminationHistory(termId),
  });
  if (q.isLoading) return <Loading />;
  if (q.error)
    return <ErrorPanel message={(q.error as Error).message} />;
  const events: WorkflowEventEntry[] = q.data ?? [];
  return (
    <ol className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-surface-default p-3 text-xs">
      {events.map((e) => (
        <li key={e.id} className="flex items-center gap-2">
          <span className="font-mono text-text-secondary">
            {e.occurred_at.slice(0, 16)}
          </span>
          <span className="rounded bg-surface-muted px-1.5 py-0.5">
            {e.transition}
          </span>
          <span className="text-text-secondary">
            {e.from_state ?? "∅"} → {e.to_state}
          </span>
          {e.actor_clerk_user_id && (
            <span className="text-text-secondary">
              by {e.actor_clerk_user_id}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

function ApprovalFlowSection({
  legalEntityId,
  employeeId,
  activeStepIndex,
}: {
  legalEntityId: string;
  employeeId: string;
  activeStepIndex: number;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["approval-flow", legalEntityId, employeeId],
    queryFn: () =>
      api.resolveApprovalChainPreview(
        legalEntityId,
        "termination",
        employeeId,
      ),
    retry: false,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) {
    // 404 → no chain configured for this LE/workflow_type.
    return (
      <p className="rounded-lg border border-dashed border-border-subtle p-4 text-sm text-text-tertiary">
        {t("operations.eos_no_chain_msg")}{" "}
        <a
          href="/policies/approval-chains/termination"
          className="text-brand hover:underline"
        >
          {t("operations.eos_configure_chain")}
        </a>
      </p>
    );
  }
  return (
    <ApprovalFlowDiagram
      resolvedSteps={q.data ?? []}
      activeStepIndex={activeStepIndex}
    />
  );
}

export function EosDetailPage() {
  const { t } = useTranslation();
  const { terminationId } = useParams<{ terminationId: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["termination", terminationId],
    queryFn: () => api.getTermination(terminationId!),
    enabled: !!terminationId,
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data || !terminationId) {
    return <ErrorPanel message={t("operations.eos_not_found")} />;
  }
  const term = q.data;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["termination", terminationId] });
    qc.invalidateQueries({ queryKey: ["clearance", terminationId] });
    qc.invalidateQueries({ queryKey: ["filings", terminationId] });
    qc.invalidateQueries({ queryKey: ["history", terminationId] });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("operations.eos_termination_title", { id: term.id.slice(0, 8) })}
        subtitle={t("operations.eos_termination_subtitle", {
          type: term.termination_type,
          date: term.effective_date,
        })}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.eos"), href: "/operations/eos" },
          { label: term.id.slice(0, 8) },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <WorkflowStateBadge state={term.workflow_state} />
            <StateAction term={term} onChanged={refresh} />
          </div>
        }
      />

      <EosNineTabBar />

      <EosTabContent
        term={term}
        terminationId={terminationId}
        refresh={refresh}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// M3X.EOS F.20 — 9-tab structure
// ---------------------------------------------------------------------------

const EOS_TABS = [
  { key: "eos-details", labelKey: "eos_tab.details", label: "EOS Details" },
  { key: "working-days", labelKey: "eos_tab.working_days", label: "Working Days" },
  { key: "eos-comp", labelKey: "eos_tab.eos_comp", label: "EOS Compensation" },
  { key: "leave-days", labelKey: "eos_tab.leave_days", label: "Leave Days" },
  { key: "loan-calc", labelKey: "eos_tab.loan_calc", label: "Loan Calculation" },
  { key: "violations", labelKey: "eos_tab.violations", label: "Violations" },
  { key: "notice-period", labelKey: "eos_tab.notice_period", label: "Notice Period" },
  { key: "gov-filings", labelKey: "eos_tab.gov_filings", label: "Gov Filings" },
  { key: "financial-dim", labelKey: "eos_tab.financial_dim", label: "Financial Dimensions" },
] as const;

function EosNineTabBar() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "eos-details";
  return (
    <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-0">
      {EOS_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          // Stable testid so UI scenarios can target a specific tab
          // without colliding with the same label in the workspace
          // shell nav (e.g. "Gov Filings" tab vs "Gov Filings Queue"
          // in the End of Service section).
          data-testid={`eos-detail-tab-${tab.key}`}
          onClick={() => {
            params.set("tab", tab.key);
            setParams(params);
          }}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
            active === tab.key
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-text-tertiary hover:text-text-secondary"
          }`}
        >
          {t(tab.labelKey, tab.label)}
        </button>
      ))}
    </div>
  );
}

function EosTabContent({
  term,
  terminationId,
  refresh,
}: {
  term: TerminationDetail;
  terminationId: string;
  refresh: () => void;
}) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const tab = params.get("tab") || "eos-details";

  switch (tab) {
    case "eos-details":
      return (
        <>
          <EosSettlementCard term={term} />
          <React.Suspense fallback={null}>
            <Article81ProtectionBadgeLazy employeeId={term.employee_id} />
          </React.Suspense>
          <section>
            <h2 className="mb-2 text-sm font-semibold text-text-primary">
              {t("operations.eos_approval_flow", "Approval flow")}
            </h2>
            <ApprovalFlowSection
              legalEntityId={term.legal_entity_id}
              employeeId={term.employee_id}
              activeStepIndex={term.approval_step_index}
            />
          </section>
          <section>
            <h2 className="mb-2 text-sm font-semibold text-text-primary">
              {t("operations.eos_workflow_timeline", "Timeline")}
            </h2>
            <HistoryCard termId={terminationId} />
          </section>
        </>
      );
    case "working-days":
      return <WorkingDaysTab term={term} />;
    case "eos-comp":
      return (
        <React.Suspense fallback={null}>
          <Eos12ComponentBreakdownPreview term={term} />
        </React.Suspense>
      );
    case "leave-days":
      return <LeaveDaysTab term={term} />;
    case "loan-calc":
      return <LoanCalcTab term={term} />;
    case "violations":
      return <ViolationsTab term={term} />;
    case "notice-period":
      return term.eos && term.eos.last_working_day ? (
        <React.Suspense fallback={null}>
          <NoticePeriodSubWorkflowLazy
            terminationId={term.id}
            noticeStartDate={term.eos.last_working_day}
            lastWorkingDay={term.eos.last_working_day}
          />
        </React.Suspense>
      ) : (
        <p className="text-sm text-text-tertiary">
          {t(
            "eos_tab.notice_no_eos",
            "Notice period sub-workflow requires the EOS calculation step first.",
          )}
        </p>
      );
    case "gov-filings":
      return (
        <>
          <FilingsCard termId={terminationId} />
          <ClearanceCard termId={terminationId} onChanged={refresh} />
        </>
      );
    case "financial-dim":
      return <FinancialDimensionsTab term={term} />;
    default:
      return null;
  }
}

function WorkingDaysTab({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
      <h3 className="mb-3 text-base font-semibold">
        {t("eos_tab.working_days_title", "Working Days Calculation")}
      </h3>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-text-secondary">
          {t("eos_tab.service_years", "Service years")}
        </dt>
        <dd className="font-mono">{term.eos?.service_years ?? "—"}</dd>
        <dt className="text-text-secondary">
          {t("eos_tab.service_months", "Service months")}
        </dt>
        <dd className="font-mono">{term.eos?.service_months ?? "—"}</dd>
        <dt className="text-text-secondary">
          {t("eos_tab.last_working_day", "Last working day")}
        </dt>
        <dd className="font-mono">{term.eos?.last_working_day ?? "—"}</dd>
        <dt className="text-text-secondary">
          {t("eos_tab.effective_date", "Effective date")}
        </dt>
        <dd className="font-mono">{term.effective_date ?? "—"}</dd>
      </dl>
    </div>
  );
}

function LeaveDaysTab({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
      <h3 className="mb-3 text-base font-semibold">
        {t("eos_tab.leave_days_title", "Leave Days Calculation")}
      </h3>
      <p className="text-xs text-text-secondary">
        {t(
          "eos_tab.leave_days_body",
          "Residual leave balance encashed as part of the final settlement.",
        )}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-text-secondary">
          {t("eos_tab.leave_encash_amount", "Leave encashment amount")}
        </dt>
        <dd className="font-mono">
          {term.settlement?.leave_encashment_amount ?? "0"}{" "}
          {term.settlement?.currency ?? "SAR"}
        </dd>
        <dt className="text-text-secondary">
          {t("eos_tab.ticket_amount", "Ticket entitlement")}
        </dt>
        <dd className="font-mono">
          {term.settlement?.ticket_entitlement_value ?? "0"}{" "}
          {term.settlement?.currency ?? "SAR"}
        </dd>
      </dl>
    </div>
  );
}

function LoanCalcTab({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
      <h3 className="mb-3 text-base font-semibold">
        {t("eos_tab.loan_calc_title", "Loan Calculation")}
      </h3>
      <p className="text-xs text-text-secondary">
        {t(
          "eos_tab.loan_calc_body",
          "Outstanding loan balance recovered from settlement (Article 90 cap-aware).",
        )}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-text-secondary">
          {t("eos_tab.outstanding_loans", "Outstanding loans")}
        </dt>
        <dd className="font-mono text-rose-700">
          −{term.settlement?.outstanding_loan_balance ?? "0"}{" "}
          {term.settlement?.currency ?? "SAR"}
        </dd>
      </dl>
    </div>
  );
}

function ViolationsTab({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
      <h3 className="mb-3 text-base font-semibold">
        {t("eos_tab.violations_title", "Violations Calculation")}
      </h3>
      <p className="text-xs text-text-secondary">
        {t(
          "eos_tab.violations_body",
          "Pending disciplinary deductions applied to the final settlement.",
        )}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-text-secondary">
          {t("eos_tab.pending_deductions", "Pending deductions")}
        </dt>
        <dd className="font-mono text-rose-700">
          −{term.settlement?.pending_deductions ?? "0"}{" "}
          {term.settlement?.currency ?? "SAR"}
        </dd>
      </dl>
    </div>
  );
}

function FinancialDimensionsTab({ term }: { term: TerminationDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
      <h3 className="mb-3 text-base font-semibold">
        {t("eos_tab.financial_dim_title", "Financial Dimensions")}
      </h3>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-text-secondary">
          {t("eos_tab.legal_entity", "Legal Entity")}
        </dt>
        <dd className="font-mono">{term.legal_entity_id}</dd>
        <dt className="text-text-secondary">
          {t("eos_tab.employee", "Employee")}
        </dt>
        <dd className="font-mono">{term.employee_id}</dd>
        <dt className="text-text-secondary">
          {t("eos_tab.total_settlement", "Total settlement")}
        </dt>
        <dd className="font-mono font-semibold">
          {term.settlement?.total_settlement_amount ?? "0"}{" "}
          {term.settlement?.currency ?? "SAR"}
        </dd>
      </dl>
      <p className="mt-3 text-xs text-text-tertiary">
        {t(
          "eos_tab.financial_dim_body",
          "Department / cost-center dimensions for ledger posting (deepens with M3X.PAYROLL ledger integration).",
        )}
      </p>
    </div>
  );
}
